import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { isClusterManaged } from '@cast/shared-cluster-context-data-access';
import type { enhanceClusterSummary } from '@cast/shared-cost-data-access';
import {
  AgentStatus,
  Cluster,
  ClusterDisplayStatus,
  ClusterOptimization,
  ClustersTimedEfficiencyItem,
} from '@cast/types';
import { toNumOrZero, type MemoryUnit } from '@cast/utils';

import { getMonthlyCostFromSummary } from './clusterMonthlyCost';
import { buildFleetMemoryMetrics } from './fleetMemoryMetrics';
import { getMonthlySavingsFromOptimization } from './clusterOptimizations';

dayjs.extend(utc);

type EnhancedClusterSummary = NonNullable<
  ReturnType<typeof enhanceClusterSummary>
>;

const getMonthlyClusterCost = (summary: EnhancedClusterSummary): number =>
  getMonthlyCostFromSummary(summary) ?? 0;

const getUtilizationPct = (used: number, allocatable: number): number =>
  allocatable > 0 ? (used / allocatable) * 100 : 0;

const getCpuWastePercentage = (summary: EnhancedClusterSummary): number => {
  const provisioned = summary.cpuProvisionedTotal.toNumber();
  const requested = summary.cpuRequestedTotal.toNumber();

  return provisioned > 0 ? ((provisioned - requested) / provisioned) * 100 : 0;
};

type DeriveFleetMetricsArgs = {
  clusters: Cluster[];
  summaries?: EnhancedClusterSummary[];
};

type FleetMetricsResult = {
  stats: {
    total: number;
    online: number;
    managed: number;
    providerCounts: Map<string, number>;
    providerCostMap: Map<string, number>;
    totalNodes: number;
    spotNodes: number;
    onDemandNodes: number;
    fallbackNodes: number;
    spotCostHourly: number;
    onDemandCostHourly: number;
    fallbackCostHourly: number;
    cpuUtilPct: number;
    memUtilPct: number;
  };
  resources: {
    cpu: {
      connected: number;
      discovered: number;
      used: number;
      unit: 'vCPU';
    };
    memory: {
      connected: number;
      discovered: number;
      used: number;
      unit: MemoryUnit;
      discoveredUnit: `/${MemoryUnit}`;
      connectedTiB: number;
      discoveredTiB: number;
      usedTiB: number;
    };
    gpu: {
      connected: number;
      discovered: number;
      used: number;
      unit: 'GPUs';
    };
  };
  nodeCosts: {
    spotMonthlyCost: number;
    onDemandMonthlyCost: number;
    fallbackMonthlyCost: number;
  };
};

export const deriveFleetMetrics = ({
  clusters,
  summaries = [],
}: DeriveFleetMetricsArgs): FleetMetricsResult => {
  const summaryByClusterId = new Map(
    summaries
      .filter((summary) => summary.clusterId)
      .map((summary) => [summary.clusterId!, summary] as const)
  );

  const providerCounts = new Map<string, number>();
  const providerCostMap = new Map<string, number>();

  clusters.forEach((cluster) => {
    const provider = (cluster.providerType ?? 'unknown').toLowerCase();
    providerCounts.set(provider, (providerCounts.get(provider) ?? 0) + 1);

    const summary = cluster.id ? summaryByClusterId.get(cluster.id) : undefined;
    if (summary) {
      const monthly = getMonthlyClusterCost(summary);
      providerCostMap.set(
        provider,
        (providerCostMap.get(provider) ?? 0) + monthly
      );
    }
  });

  let totalNodes = 0;
  let spotNodes = 0;
  let onDemandNodes = 0;
  let fallbackNodes = 0;
  let spotCostHourly = 0;
  let onDemandCostHourly = 0;
  let fallbackCostHourly = 0;
  let connectedCpuAlloc = 0;
  let discoveredCpuAlloc = 0;
  let totalCpuUsed = 0;
  let connectedMemAlloc = 0;
  let discoveredMemAlloc = 0;
  let totalMemUsed = 0;
  let connectedGpuAlloc = 0;
  let discoveredGpuAlloc = 0;
  let totalGpuUsed = 0;
  let hasSummaries = false;

  clusters.forEach((cluster) => {
    const summary = cluster.id ? summaryByClusterId.get(cluster.id) : undefined;
    if (!summary) {
      return;
    }

    hasSummaries = true;
    totalNodes += summary.nodesCountTotal.toNumber();
    spotNodes += summary.nodeCountSpot.toNumber();
    onDemandNodes += summary.nodeCountOnDemand.toNumber();
    fallbackNodes += summary.nodeCountSpotFallbackCastai.toNumber();
    spotCostHourly += summary.costHourlySpot.toNumber();
    onDemandCostHourly += summary.costHourlyOnDemand.toNumber();
    fallbackCostHourly += summary.costHourlySpotFallback.toNumber();
    const cpuAlloc = summary.cpuAllocatableTotal.toNumber();
    const memAlloc = summary.ramAllocatableTotal.toNumber();
    const gpuAlloc = summary.gpuAllocatableTotal.toNumber();
    const isDiscovered =
      cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;

    if (isDiscovered) {
      discoveredCpuAlloc += cpuAlloc;
      discoveredMemAlloc += memAlloc;
      discoveredGpuAlloc += gpuAlloc;
    } else {
      connectedCpuAlloc += cpuAlloc;
      connectedMemAlloc += memAlloc;
      connectedGpuAlloc += gpuAlloc;
      totalCpuUsed += summary.cpuUsed.toNumber();
      totalMemUsed += summary.ramUsed.toNumber();
      totalGpuUsed += summary.gpuUsedTotal.toNumber();
    }
  });

  if (!hasSummaries) {
    totalCpuUsed = clusters.reduce(
      (sum, cluster) => sum + (cluster.metrics?.billableCpus ?? 0),
      0
    );
  }

  const stats = {
    total: clusters.length,
    online: clusters.filter(
      (cluster) => cluster.agentStatus === AgentStatus.ONLINE
    ).length,
    managed: clusters.filter((cluster) => isClusterManaged(cluster)).length,
    providerCounts,
    providerCostMap,
    totalNodes,
    spotNodes,
    onDemandNodes,
    fallbackNodes,
    spotCostHourly,
    onDemandCostHourly,
    fallbackCostHourly,
    cpuUtilPct: getUtilizationPct(totalCpuUsed, connectedCpuAlloc),
    memUtilPct: getUtilizationPct(totalMemUsed, connectedMemAlloc),
  };

  const nodeCosts = {
    spotMonthlyCost: spotCostHourly * AVERAGE_HOURS_IN_MONTH,
    onDemandMonthlyCost: onDemandCostHourly * AVERAGE_HOURS_IN_MONTH,
    fallbackMonthlyCost: fallbackCostHourly * AVERAGE_HOURS_IN_MONTH,
  };

  const resources = {
    cpu: {
      connected: connectedCpuAlloc,
      discovered: discoveredCpuAlloc,
      used: totalCpuUsed,
      unit: 'vCPU' as const,
    },
    memory: buildFleetMemoryMetrics({
      connectedGiB: connectedMemAlloc,
      discoveredGiB: discoveredMemAlloc,
      usedGiB: totalMemUsed,
    }),
    gpu: {
      connected: connectedGpuAlloc,
      discovered: discoveredGpuAlloc,
      used: totalGpuUsed,
      unit: 'GPUs' as const,
    },
  };

  return { stats, resources, nodeCosts };
};

export const deriveUtilizationSparkData = (
  items: ClustersTimedEfficiencyItem[]
): {
  utilizationSparkData: {
    timestamp: string;
    chartTimestampUnix: number;
    cpuUtilization: number;
    memoryUtilization: number;
  }[];
  utilizationSparkDomain: readonly [number, number];
} => {
  const utilizationSparkData = items.map((item) => {
    const timestamp = dayjs
      .utc(item.timestamp)
      .subtract(1, 'day')
      .startOf('day');
    const cpuProvisioned = toNumOrZero(item.cpuResources?.provisioned);
    const cpuUsed = toNumOrZero(item.cpuResources?.used);
    const memProvisioned = toNumOrZero(item.ramResources?.provisioned);
    const memUsed = toNumOrZero(item.ramResources?.used);

    return {
      timestamp: timestamp.toISOString(),
      chartTimestampUnix: timestamp.unix(),
      cpuUtilization: cpuProvisioned > 0 ? (cpuUsed / cpuProvisioned) * 100 : 0,
      memoryUtilization:
        memProvisioned > 0 ? (memUsed / memProvisioned) * 100 : 0,
    };
  });

  const allValues = utilizationSparkData.flatMap((point) => [
    point.cpuUtilization,
    point.memoryUtilization,
  ]);
  const min = allValues.length ? Math.max(0, Math.min(...allValues) - 4) : 0;
  const max = allValues.length
    ? Math.min(100, Math.max(...allValues) + 4)
    : 100;

  return {
    utilizationSparkData,
    utilizationSparkDomain: [min, max] as const,
  };
};

export const deriveEstimatedSavingsMap = (
  summaries: EnhancedClusterSummary[],
  optimizationsByClusterId: ReadonlyMap<string, ClusterOptimization> = new Map()
): Map<string, { monthlySpend: number; wastePercentage: number }> => {
  const map = new Map<
    string,
    { monthlySpend: number; wastePercentage: number }
  >();

  summaries.forEach((summary) => {
    if (!summary.clusterId) {
      return;
    }

    const monthlySpend = getMonthlyClusterCost(summary);
    const optimization = optimizationsByClusterId.get(summary.clusterId);
    const monthlySavings = optimization
      ? getMonthlySavingsFromOptimization(optimization)
      : null;

    const wastePercentage =
      monthlySavings != null && monthlySpend > 0
        ? (monthlySavings / monthlySpend) * 100
        : getCpuWastePercentage(summary);

    map.set(summary.clusterId, {
      monthlySpend,
      wastePercentage,
    });
  });

  return map;
};

export const deriveSummaryMap = (
  summaries: EnhancedClusterSummary[]
): Map<
  string,
  { clusterScore: string; nodesCountTotal: { toNumber(): number } }
> => {
  const map = new Map<
    string,
    { clusterScore: string; nodesCountTotal: { toNumber(): number } }
  >();

  summaries.forEach((summary) => {
    if (!summary.clusterId) {
      return;
    }

    map.set(summary.clusterId, {
      clusterScore: summary.clusterScore,
      nodesCountTotal: summary.nodesCountTotal,
    });
  });

  return map;
};
